import { InternalThemeDefinition } from "vuetify/lib/composables/theme";

export interface BaseColorTokens {
  "primary": string;
  "on-primary": string;
  "secondary": string;
  "on-secondary": string;
  "error": string;
  "on-error": string;
  "background": string;
  "on-background": string;
  "surface": string;
  "on-surface": string;
  "info": string;
  "on-info": string;
  "success": string;
  "on-success": string;
  "warning": string;
  "on-warning": string;
  "dark-background": string;
  "on-dark-background": string;
  "content-background": string;
  "on-content-background": string;
  "stripes-background": string;
  "on-stripes-background": string;
  "tertiary"?: string;
  "on-tertiary"?: string;
  "heading"?: string;
  "body"?: string;
}

/**
 * Roles that default to a base token (see SEMANTIC_COLOR_FALLBACKS) but can be
 * overridden per-tenant when a producer needs e.g. a header color different from
 * the footer, without touching every theme that's happy with the default.
 */
export interface SemanticColorTokens {
  "header-background": string;
  "on-header-background": string;
  "footer-background": string;
  "on-footer-background": string;
}

export type ThemeColorInput = BaseColorTokens & Partial<SemanticColorTokens>;

export interface ThemeInput {
  dark: boolean;
  colors: ThemeColorInput;
  variables?: Record<string, string | number>;
}

const SEMANTIC_COLOR_FALLBACKS: { [K in keyof SemanticColorTokens]: keyof BaseColorTokens } = {
  "header-background": "dark-background",
  "on-header-background": "on-dark-background",
  "footer-background": "dark-background",
  "on-footer-background": "on-dark-background",
};

export function resolveTheme(input: ThemeInput): InternalThemeDefinition {
  const colors = { ...input.colors } as ThemeColorInput & SemanticColorTokens;

  for (const semanticKey of Object.keys(SEMANTIC_COLOR_FALLBACKS) as (keyof SemanticColorTokens)[]) {
    colors[semanticKey] ??= colors[SEMANTIC_COLOR_FALLBACKS[semanticKey]]!;
  }

  return {
    dark: input.dark,
    colors,
    variables: input.variables ?? {},
  } as unknown as InternalThemeDefinition;
}
